import type { HttpClient } from "../httpClient";
import { createEndpoint, StandardSuccessResponseSchema } from "../createEndpoint";
import { ProfileListingSchema, HasExpertProfileSchema, PROFILE_STATUS, ExpertListingRequestSchema, type ExpertListingRequest } from "../schemas/expert";
import { PackagedServiceSchema } from "../schemas/packaged-service";
import { CustomerStorySchema } from "../schemas/customer-story";
import { FaqSchema } from "../schemas/faq";
import { z } from "zod";
import {
    CustomerStoryCardSchema,
    ExpertCustomerStoryDetailsSchema,
    CustomerStoryIndustrySchema,
    CustomerStoryUpsertRequestSchema,
    type CustomerStoryUpsertRequest,
} from "../schemas/customer-stories";

export function createExpertApi(http: HttpClient, baseUrl: string) {
    const expertBaseUrl = `${baseUrl}/expert`;
    return {
        getProfileDetails: createEndpoint(http, 'GET', '/personal-and-business-details', {
            responseSchema: ProfileListingSchema,
            base: expertBaseUrl,
        }),

        hasExpertProfile: createEndpoint(http, 'GET', '/is-user-has-expert', {
            base: expertBaseUrl,
            responseSchema: HasExpertProfileSchema,
        }),

        createExpertProfile: createEndpoint(http, 'POST', '/migrate-users-data', {
            base: expertBaseUrl,
            responseSchema: StandardSuccessResponseSchema,
        }),

        getPackagedServices: createEndpoint(http, 'GET', '/packaged-services', {
            base: expertBaseUrl,
            responseSchema: z.array(PackagedServiceSchema),
        }),

        getCustomerStories: createEndpoint(http, 'GET', '/customer-stories', {
            base: expertBaseUrl,
            responseSchema: z.array(CustomerStorySchema),
        }),

        getCustomerStoriesV2: createEndpoint(http, 'GET', '/customer-stories', {
            base: expertBaseUrl,
            responseSchema: z.array(CustomerStoryCardSchema),
        }),

        getCustomerStory: createEndpoint(http, 'GET', '/customer-stories/:story', {
            base: expertBaseUrl,
            responseSchema: ExpertCustomerStoryDetailsSchema,
            params: ['story'],
        }),

        createCustomerStory: createEndpoint<z.infer<typeof ExpertCustomerStoryDetailsSchema>, CustomerStoryUpsertRequest>(http, 'POST', '/customer-stories', {
            base: expertBaseUrl,
            responseSchema: ExpertCustomerStoryDetailsSchema,
        }),

        updateCustomerStory: createEndpoint<z.infer<typeof ExpertCustomerStoryDetailsSchema>, Partial<CustomerStoryUpsertRequest>>(http, 'PUT', '/customer-stories/:story', {
            base: expertBaseUrl,
            responseSchema: ExpertCustomerStoryDetailsSchema,
            params: ['story'],
        }),

        deleteCustomerStory: createEndpoint<{ success: true; message?: string }, never>(http, 'DELETE', '/customer-stories/:story', {
            base: expertBaseUrl,
            responseSchema: StandardSuccessResponseSchema,
            params: ['story'],
        }),

        getCustomerStoryIndustries: createEndpoint(http, 'GET', '/industries', {
            base: baseUrl,
            responseSchema: z.array(CustomerStoryIndustrySchema),
        }),

        getFaqs: createEndpoint(http, 'GET', '/faqs', {
            base: expertBaseUrl,
            responseSchema: z.array(FaqSchema),
        }),

        setProfileStatusToPublished: createEndpoint(http, 'POST', '/publish-profile', {
            base: expertBaseUrl,
            responseSchema: z.object({
                profile_status: z.literal(PROFILE_STATUS.PUBLISHED),
            }) 
        }),


        saveProfileDetails: createEndpoint<z.infer<typeof StandardSuccessResponseSchema>, ExpertListingRequest>(http, 'POST', '/expert-listing', {
            base: expertBaseUrl,
            responseSchema: StandardSuccessResponseSchema,
        }),

        /**
         * @deprecated use saveProfileDetails instead, this is left for backward compatibility, but the name is confusing
         */
        publishProfileDetails: createEndpoint<z.infer<typeof StandardSuccessResponseSchema>, ExpertListingRequest>(http, 'POST', '/expert-listing', {
            base: expertBaseUrl,
            responseSchema: StandardSuccessResponseSchema,
        }),
    }
}
